// ═══════════════════════════════════════════════════════════════════════════
// TARIFS — la grille en vigueur, les changements programmés, l'historique,
// et le relevé du mois (21/10/2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// « Les tarifs ne doivent pas être hardcodés, on doit pouvoir les modifier à
// tout moment » (fondateur). Ici, un changement ne RÉÉCRIT jamais un prix :
// il ouvre une nouvelle version, tout de suite ou à une date future, et le
// serveur clôt la précédente. Le motif est obligatoire, et chaque changement est
// scellé dans la chaîne d'audit. Aucun calcul de prix n'est fait dans l'écran.
import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import {
  useGrille, useHistorique, useNouveauTarif, useReleve, useUnites,
  type Palier, type Tarif,
} from '../api/facturation';

const SYSTEME = '00000000-0000-0000-0000-000000000001';

function libelleClient(tenant: string | null): string {
  if (!tenant) return 'Grille par défaut';
  if (tenant === SYSTEME) return 'Mowibo (interne)';
  return `Client ${tenant.slice(0, 8)}…`;
}

function format(montant: string | null, devise: string | null): string {
  if (montant === null || devise === null) return 'prix non fixé';
  const n = Number(montant);
  return devise === 'GNF'
    ? `${Math.round(n).toLocaleString('fr-FR')} GNF`
    : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function dateCourte(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

function moisCourant(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function messageErreur(e: unknown): string {
  const ax = e as AxiosError<{ detail?: unknown }>;
  const d = ax?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return 'Le changement n’a pas été enregistré. Vérifiez les champs et réessayez.';
}

interface Edition { unite: string; tenant: string | null; actuel: Tarif | null }

function FormulaireTarif({ edition, fermer }: { edition: Edition; fermer: () => void }) {
  const enregistrer = useNouveauTarif();
  const [montant, setMontant] = useState(edition.actuel?.montant ?? '');
  const [devise, setDevise] = useState<'GNF' | 'EUR'>(edition.actuel?.devise ?? 'GNF');
  const [tenant, setTenant] = useState(edition.tenant ?? '');
  const [quand, setQuand] = useState<'maintenant' | 'programme'>('maintenant');
  const [date, setDate] = useState('');
  const [paliers, setPaliers] = useState<Palier[]>(edition.actuel?.paliers ?? []);
  const [motif, setMotif] = useState('');

  const valide = montant !== '' && Number(montant) >= 0 && motif.trim().length >= 5
    && (quand === 'maintenant' || date !== '');

  const envoyer = () => {
    enregistrer.mutate({
      unite: edition.unite,
      montant,
      devise,
      tenant_id: tenant.trim() || null,
      paliers,
      valable_du: quand === 'programme' ? new Date(date).toISOString() : null,
      motif: motif.trim(),
    }, { onSuccess: fermer });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="glass-panel w-full max-w-lg space-y-4 p-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Changer le prix — {edition.unite}</h2>
          <p className="mt-1 text-xs text-gray-500">
            {edition.actuel
              ? `En vigueur : ${format(edition.actuel.montant, edition.actuel.devise)} depuis le ${dateCourte(edition.actuel.valable_du)}`
              : 'Aucun prix en vigueur pour cette ligne.'}
            {' '}Une nouvelle version sera ouverte ; l’actuelle sera close à la date d’effet.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-2 text-xs text-gray-400">Montant
            <input type="number" min="0" step="any" value={montant} onChange={(e) => setMontant(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-tertiary px-3 py-2 text-sm text-gray-200" />
          </label>
          <label className="text-xs text-gray-400">Devise
            <select value={devise} onChange={(e) => setDevise(e.target.value as 'GNF' | 'EUR')}
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-tertiary px-3 py-2 text-sm text-gray-200">
              <option value="GNF">GNF</option>
              <option value="EUR">EUR</option>
            </select>
          </label>
        </div>

        <label className="block text-xs text-gray-400">Client (identifiant du tenant — vide pour la grille par défaut)
          <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="grille par défaut"
            className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-tertiary px-3 py-2 font-mono text-xs text-gray-200" />
        </label>

        <div className="space-y-2">
          <p className="text-xs text-gray-400">Date d’effet</p>
          <div className="flex gap-4 text-sm text-gray-300">
            <label className="flex items-center gap-2">
              <input type="radio" checked={quand === 'maintenant'} onChange={() => setQuand('maintenant')} /> Immédiate
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={quand === 'programme'} onChange={() => setQuand('programme')} /> Programmée
            </label>
          </div>
          {quand === 'programme' && (
            <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg border border-border-subtle bg-surface-tertiary px-3 py-2 text-sm text-gray-200" />
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">Paliers de volume (remise à partir du N-ième appel du mois)</p>
            <button type="button" onClick={() => setPaliers([...paliers, { a_partir_de: 100000, remise_pct: 30 }])}
              className="text-xs text-cyber-cyan">+ Ajouter</button>
          </div>
          {paliers.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
              <span>à partir de</span>
              <input type="number" min="1" value={p.a_partir_de}
                onChange={(e) => setPaliers(paliers.map((x, j) => (j === i ? { ...x, a_partir_de: Number(e.target.value) } : x)))}
                className="w-28 rounded border border-border-subtle bg-surface-tertiary px-2 py-1" />
              <span>appels : −</span>
              <input type="number" min="1" max="100" value={p.remise_pct}
                onChange={(e) => setPaliers(paliers.map((x, j) => (j === i ? { ...x, remise_pct: Number(e.target.value) } : x)))}
                className="w-16 rounded border border-border-subtle bg-surface-tertiary px-2 py-1" />
              <span>%</span>
              <button type="button" onClick={() => setPaliers(paliers.filter((_, j) => j !== i))} className="ml-auto text-red-400">Retirer</button>
            </div>
          ))}
        </div>

        <label className="block text-xs text-gray-400">Motif (obligatoire — il est scellé dans l’audit)
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2}
            className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-tertiary px-3 py-2 text-sm text-gray-200" />
        </label>

        {enregistrer.isError && <p className="text-sm text-red-400">{messageErreur(enregistrer.error)}</p>}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={fermer} className="px-4 py-2 text-sm text-gray-400">Annuler</button>
          <button type="button" disabled={!valide || enregistrer.isPending} onClick={envoyer}
            className="rounded-lg bg-cyber-cyan/20 px-4 py-2 text-sm font-medium text-cyber-cyan disabled:opacity-40">
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer la nouvelle version'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Historique({ unite, tenant, fermer }: { unite: string; tenant: string | null; fermer: () => void }) {
  const { data, isLoading } = useHistorique(unite, tenant);
  return (
    <div className="glass-panel space-y-3 p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-200">Historique — {unite} · {libelleClient(tenant)}</h3>
        <button type="button" onClick={fermer} className="text-xs text-gray-400">Fermer</button>
      </div>
      {isLoading && <p className="text-xs text-gray-500">Chargement…</p>}
      {data?.map((v) => (
        <div key={v.id} className="border-t border-border-subtle pt-2 text-xs text-gray-400">
          <span className="font-medium text-gray-200">{format(v.montant, v.devise)}</span>
          {' '}du {dateCourte(v.valable_du)}{v.valable_au ? ` au ${dateCourte(v.valable_au)}` : ' — en cours'}
          <p className="mt-0.5 text-gray-500">« {v.motif} » — {v.cree_par}</p>
        </div>
      ))}
    </div>
  );
}

function ReleveDuMois() {
  const [mois, setMois] = useState(moisCourant());
  const { data, isLoading, isError } = useReleve(mois);
  return (
    <div className="glass-panel space-y-3 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-100">Relevé du mois</h2>
        <input type="month" value={mois} onChange={(e) => setMois(e.target.value)}
          className="rounded-lg border border-border-subtle bg-surface-tertiary px-3 py-1.5 text-sm text-gray-200" />
      </div>
      <p className="text-xs text-gray-500">
        Lu dans la chaîne d’audit : seuls les appels réussis d’une unité facturable comptent, au prix en vigueur à la date de chaque appel.
      </p>
      {isLoading && <p className="text-xs text-gray-500">Chargement…</p>}
      {isError && <p className="text-xs text-red-400">Le relevé n’a pas pu être lu.</p>}
      {data && data.lignes.length === 0 && <p className="text-sm text-gray-500">Aucun appel facturable ce mois-ci.</p>}
      {data && data.lignes.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500">
            <tr><th className="text-left">Client</th><th className="text-left">Unité</th><th className="text-right">Quantité</th><th className="text-right">Montant</th></tr>
          </thead>
          <tbody className="text-gray-300">
            {data.lignes.map((l, i) => (
              <tr key={i} className="border-t border-border-subtle">
                <td className="py-1.5">{libelleClient(l.tenant)}</td>
                <td>{l.unite}</td>
                <td className="text-right">{l.quantite.toLocaleString('fr-FR')}</td>
                <td className={`text-right ${l.montant === null ? 'text-amber-400' : ''}`}>{format(l.montant, l.devise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && data.totaux.map((t) => (
        <p key={`${t.tenant}-${t.devise}`} className="text-right text-sm text-gray-200">
          Total {libelleClient(t.tenant)} : <span className="font-semibold">{format(t.montant, t.devise)}</span>
        </p>
      ))}
      {data && data.unites_sans_prix.length > 0 && (
        <p className="text-xs text-amber-400">
          Prix non fixé pour : {data.unites_sans_prix.join(', ')} — aucun total n’est publié tant qu’une ligne n’est pas tarifée.
        </p>
      )}
    </div>
  );
}

export default function TarifsPage() {
  const { data: grille, isLoading, isError } = useGrille();
  const { data: unites } = useUnites();
  const [edition, setEdition] = useState<Edition | null>(null);
  const [historique, setHistorique] = useState<{ unite: string; tenant: string | null } | null>(null);

  const parUnite = useMemo(() => {
    const m = new Map<string, Tarif[]>();
    (unites ?? []).forEach((u) => m.set(u, []));
    grille?.tarifs.forEach((t) => m.set(t.unite, [...(m.get(t.unite) ?? []), t]));
    return m;
  }, [grille, unites]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Tarifs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Prix par unité facturable. Un changement ouvre une nouvelle version — immédiate ou programmée — et ne réécrit jamais l’ancienne.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}
      {isError && <p className="text-sm text-red-400">La grille n’a pas pu être lue.</p>}

      {grille && (
        <div className="glass-panel overflow-x-auto p-5">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500">
              <tr>
                <th className="text-left">Unité</th>
                <th className="text-left">Client</th>
                <th className="text-right">Prix</th>
                <th className="text-left pl-4">Paliers</th>
                <th className="text-left">Depuis</th>
                <th />
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {[...parUnite.entries()].map(([unite, lignes]) => {
                const rangees = lignes.length ? lignes : [null];
                return rangees.map((t, i) => (
                  <tr key={`${unite}-${t?.id ?? 'vide'}`} className="border-t border-border-subtle">
                    <td className="py-2 font-mono text-xs">{i === 0 ? unite : ''}</td>
                    <td>{t ? libelleClient(t.tenant_id) : '—'}</td>
                    <td className={`text-right ${t ? '' : 'text-amber-400'}`}>{t ? format(t.montant, t.devise) : 'prix non fixé'}</td>
                    <td className="pl-4 text-xs text-gray-500">
                      {t?.paliers.length ? t.paliers.map((p) => `≥${p.a_partir_de.toLocaleString('fr-FR')} : −${p.remise_pct} %`).join(' · ') : '—'}
                    </td>
                    <td className="text-xs text-gray-500">{t ? dateCourte(t.valable_du) : ''}</td>
                    <td className="space-x-3 text-right text-xs">
                      {t && <button type="button" onClick={() => setHistorique({ unite, tenant: t.tenant_id })} className="text-gray-400">Historique</button>}
                      <button type="button" onClick={() => setEdition({ unite, tenant: t?.tenant_id ?? null, actuel: t })} className="text-cyber-cyan">
                        {t ? 'Changer' : 'Fixer un prix'}
                      </button>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {grille && grille.programmes.length > 0 && (
        <div className="glass-panel space-y-2 p-5">
          <h2 className="text-base font-semibold text-gray-100">Changements programmés</h2>
          {grille.programmes.map((t) => (
            <p key={t.id} className="text-sm text-gray-300">
              {t.unite} · {libelleClient(t.tenant_id)} → <span className="font-medium">{format(t.montant, t.devise)}</span> à partir du {dateCourte(t.valable_du)}
              <span className="text-xs text-gray-500"> — « {t.motif} »</span>
            </p>
          ))}
        </div>
      )}

      {historique && <Historique unite={historique.unite} tenant={historique.tenant} fermer={() => setHistorique(null)} />}

      <ReleveDuMois />

      {edition && <FormulaireTarif edition={edition} fermer={() => setEdition(null)} />}
    </div>
  );
}
